import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

export type AuthModalMode = 'sign-in' | 'sign-up';
export type AuthModalPhase = 'auth' | 'setup';

interface AuthModalContextValue {
  isOpen: boolean;
  mode: AuthModalMode;
  phase: AuthModalPhase;
  openAuthModal: (mode?: AuthModalMode) => void;
  continueAccountSetup: () => void;
  closeAuthModal: () => void;
  setMode: (mode: AuthModalMode) => void;
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuthModalMode>('sign-in');
  const [phase, setPhase] = useState<AuthModalPhase>('auth');

  const openAuthModal = useCallback((nextMode: AuthModalMode = 'sign-in') => {
    setMode(nextMode);
    setPhase('auth');
    setIsOpen(true);
  }, []);

  const continueAccountSetup = useCallback(() => {
    setPhase('setup');
    setIsOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    setPhase('auth');
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      mode,
      phase,
      openAuthModal,
      continueAccountSetup,
      closeAuthModal,
      setMode,
    }),
    [isOpen, mode, phase, openAuthModal, continueAccountSetup, closeAuthModal]
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
}
